import React from "react";

/* ══════════════════════════════════════════════════════════════
   EVALORE, COGNARA, FORMIGEN — shared icon components
   Each icon mirrors the hero glyph of its product so the brand
   reads identically across hero / grid card / Cognitive Substrate.
   Theme-aware via CSS vars.
   ══════════════════════════════════════════════════════════════ */

/* EVALORE — 3×3 weighted criteria matrix + orange AI spark at (0,2). */
export function NirnayaIcon({ size = 56, className = "" }) {
  const cells = [];
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) {
      const idx = r * 3 + c;
      const isSpark = r === 0 && c === 2;
      cells.push(
        <rect
          key={idx}
          x={8 + c * 14}
          y={8 + r * 14}
          width="10"
          height="10"
          rx="1.5"
          className={isSpark ? "nr-mark-spark" : "nr-mark-cell"}
          strokeWidth={isSpark ? "0" : "1.3"}
          fill={isSpark ? "#F26B1F" : "none"}
        />
      );
    }
  }
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 56 56"
      width={size}
      height={size}
      aria-hidden="true"
      className={`nr-mark ${className}`}
    >
      {cells}
    </svg>
  );
}

/* COGNARA — 3 horizontal white strata pierced by a vertical ORANGE axis
   with orange dots at each intersection. Axis extends above top and below
   bottom strata. */
export function SutradharIcon({ size = 56, className = "" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 56 56"
      width={size}
      height={size}
      aria-hidden="true"
      className={`st-mark ${className}`}
    >
      {/* 3 horizontal strata */}
      <line x1="8" y1="16" x2="48" y2="16" className="st-mark-line" strokeWidth="2" strokeLinecap="round" />
      <line x1="8" y1="28" x2="48" y2="28" className="st-mark-line" strokeWidth="2" strokeLinecap="round" />
      <line x1="8" y1="40" x2="48" y2="40" className="st-mark-line" strokeWidth="2" strokeLinecap="round" />
      {/* Vertical orange axis — extends above & below strata */}
      <line x1="28" y1="6" x2="28" y2="50" stroke="#F26B1F" strokeWidth="2.2" strokeLinecap="round" />
      {/* Orange dots at each intersection */}
      <circle cx="28" cy="16" r="2.8" fill="#F26B1F" />
      <circle cx="28" cy="28" r="2.8" fill="#F26B1F" />
      <circle cx="28" cy="40" r="2.8" fill="#F26B1F" />
    </svg>
  );
}

/* FORMIGEN — P&ID cylindrical vessel with elliptical caps, orange PSV vent
   (3-tick safety valve) on top, blue inlet arrow (left), blue outlet to
   bowtie control valve + instrument circle (right), blue downcomer +
   pump circle (bottom), orange AI brain glow at vessel center. */
export function RachanaIcon({ size = 56, className = "" }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 56 56"
      width={size}
      height={size}
      aria-hidden="true"
      className={`rc-mark ${className}`}
    >
      {/* Vessel: straight sides + elliptical caps */}
      <ellipse cx="26" cy="14" rx="8" ry="2.8" className="rc-mark-vessel" fill="none" strokeWidth="1.4" />
      <line x1="18" y1="14" x2="18" y2="40" className="rc-mark-vessel" strokeWidth="1.4" />
      <line x1="34" y1="14" x2="34" y2="40" className="rc-mark-vessel" strokeWidth="1.4" />
      <ellipse cx="26" cy="40" rx="8" ry="2.8" className="rc-mark-vessel" fill="none" strokeWidth="1.4" />

      {/* Orange PSV (safety-valve vent) on top — 3 tick marks converging */}
      <line x1="26" y1="3" x2="26" y2="10" stroke="#F26B1F" strokeWidth="1.4" strokeLinecap="round" />
      <line x1="22" y1="6" x2="26" y2="10" stroke="#F26B1F" strokeWidth="1.3" strokeLinecap="round" />
      <line x1="30" y1="6" x2="26" y2="10" stroke="#F26B1F" strokeWidth="1.3" strokeLinecap="round" />

      {/* Blue inlet arrow from left */}
      <line x1="6" y1="22" x2="16" y2="22" className="rc-mark-flow" strokeWidth="1.4" strokeLinecap="round" />
      <polygon points="14,19.5 18,22 14,24.5" className="rc-mark-flow-fill" />

      {/* Blue outlet to bowtie control valve + instrument circle (right side) */}
      <line x1="34" y1="32" x2="40" y2="32" className="rc-mark-flow" strokeWidth="1.2" strokeLinecap="round" />
      <polygon points="40,30 44,32 40,34" fill="none" className="rc-mark-flow" strokeWidth="1" />
      <polygon points="48,30 44,32 48,34" fill="none" className="rc-mark-flow" strokeWidth="1" />
      <circle cx="50" cy="24" r="3.5" fill="none" className="rc-mark-vessel" strokeWidth="1.1" />
      <line x1="46.5" y1="24" x2="53.5" y2="24" className="rc-mark-vessel" strokeWidth="0.8" />

      {/* Blue downcomer + pump circle at bottom */}
      <line x1="26" y1="43" x2="26" y2="47" className="rc-mark-flow" strokeWidth="1.4" strokeLinecap="round" />
      <polygon points="23.5,45 26,49 28.5,45" className="rc-mark-flow-fill" />
      <circle cx="26" cy="52.5" r="2.8" fill="none" className="rc-mark-vessel" strokeWidth="1.1" />

      {/* Orange AI brain glow at vessel center */}
      <circle cx="26" cy="27" r="5" fill="#F26B1F" opacity="0.08" />
      <circle cx="26" cy="27" r="3" fill="#F26B1F" opacity="0.22" />
      <circle cx="26" cy="27" r="1.6" fill="#F26B1F" />
    </svg>
  );
}
